/*!
 * ConfigViewer Component
 * Displays configurations side-by-side for all three environments
 * Organized by domains (Core, AI, Auth) within layers (Backend, Storage, Frontend, Middleware)
 */
import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Collapse,
  UncontrolledTooltip,
} from "reactstrap";
import configManagerApi from "services/configManagerApi";

function ConfigViewer({ 
  configs, 
  permissions, 
  onConfigUpdate, 
  onReload, 
  selectedLayer,
  sessions,
  hasUnsavedChanges,
  onStartSession,
  onSaveDiff,
  onSaveSession,
  onCommitSession,
  onSaveAndCommit,
  onCancelSession
}) {
  const [editingEnv, setEditingEnv] = useState(null);
  const [editConfigs, setEditConfigs] = useState({ dev: {}, test: {}, prod: {} });
  const [originalConfigs, setOriginalConfigs] = useState({ dev: {}, test: {}, prod: {} }); // Track original values for diff
  const [fieldMapping, setFieldMapping] = useState(null);
  const [fileMapping, setFileMapping] = useState(null);
  const [loadingFields, setLoadingFields] = useState(true);
  
  // Accordion state: track which sections are expanded (all collapsed initially)
  // Structure: { domain: { section: boolean } }
  const [expandedSections, setExpandedSections] = useState({
    dev: {
      core: false,
      ai: false,
      auth: false,
    },
    test: {
      core: false,
      ai: false,
      auth: false,
    },
    prod: {
      core: false,
      ai: false,
      auth: false,
    },
  });
  
  // Refs for synchronized scrolling
  const devScrollRef = useRef(null);
  const testScrollRef = useRef(null);
  const prodScrollRef = useRef(null);
  const isScrollingRef = useRef(false);
  
  // Track visible sections for context in header
  const [visibleSections, setVisibleSections] = useState({
    dev: null,
    test: null,
    prod: null,
  });
  
  // Refs for section headers to detect visibility
  const sectionRefs = useRef({});

  useEffect(() => {
    // Initialize edit configs and original configs with current configs
    setEditConfigs({
      dev: { ...configs.dev },
      test: { ...configs.test },
      prod: { ...configs.prod },
    });
    setOriginalConfigs({
      dev: { ...configs.dev },
      test: { ...configs.test },
      prod: { ...configs.prod },
    });
  }, [configs]);

  // Load field mapping and file mapping from backend
  useEffect(() => {
    const loadMappings = async () => {
      try {
        setLoadingFields(true);
        const [fieldsResponse, filesResponse] = await Promise.all([
          configManagerApi.getLayerFields(selectedLayer),
          configManagerApi.getFileMapping(),
        ]);
        
        if (fieldsResponse.status === 'success') {
          setFieldMapping(fieldsResponse.fields);
        } else {
          console.error('Failed to load field mapping:', fieldsResponse.message);
          setFieldMapping({ core: [], ai: [], auth: [] });
        }
        
        if (filesResponse.status === 'success') {
          setFileMapping(filesResponse.files);
        } else {
          console.error('Failed to load file mapping:', filesResponse.message);
        }
      } catch (error) {
        console.error('Error loading mappings:', error);
        setFieldMapping({ core: [], ai: [], auth: [] });
      } finally {
        setLoadingFields(false);
      }
    };

    loadMappings();
  }, [selectedLayer]);

  // Reset expanded sections when layer changes
  useEffect(() => {
    setExpandedSections({
      dev: { core: false, ai: false, auth: false },
      test: { core: false, ai: false, auth: false },
      prod: { core: false, ai: false, auth: false },
    });
    // Reset visible sections
    setVisibleSections({
      dev: null,
      test: null,
      prod: null,
    });
  }, [selectedLayer]);
  
  // Clear visible sections when all sections are collapsed
  useEffect(() => {
    ['dev', 'test', 'prod'].forEach((env) => {
      const hasExpanded = Object.values(expandedSections[env] || {}).some(v => v === true);
      if (!hasExpanded) {
        setVisibleSections((prev) => ({
          ...prev,
          [env]: null,
        }));
      }
    });
  }, [expandedSections]);
  
  // Setup Intersection Observer to track visible sections
  useEffect(() => {
    const observers = {};
    
    const setupObservers = () => {
      ['dev', 'test', 'prod'].forEach((env) => {
        const scrollContainer = env === 'dev' ? devScrollRef.current : 
                               env === 'test' ? testScrollRef.current : 
                               prodScrollRef.current;
        
        if (!scrollContainer) return;
        
        // Disconnect existing observer if any
        if (observers[env]) {
          observers[env].disconnect();
        }
        
        const observer = new IntersectionObserver(
          (entries) => {
            // Find the section that's most visible (highest intersection ratio)
            let mostVisible = null;
            let maxRatio = 0;
            
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
                const sectionKey = entry.target.getAttribute('data-section');
                // Only consider sections that are expanded
                if (sectionKey && expandedSections[env]?.[sectionKey]) {
                  maxRatio = entry.intersectionRatio;
                  mostVisible = sectionKey;
                }
              }
            });
            
            // Only update if we found a visible section (with minimum threshold) and it's expanded
            if (mostVisible && maxRatio > 0.1 && expandedSections[env]?.[mostVisible]) {
              setVisibleSections((prev) => ({
                ...prev,
                [env]: mostVisible,
              }));
            }
            // Don't clear here - let scroll handler determine when to clear
          },
          {
            root: scrollContainer,
            rootMargin: '-80px 0px -50% 0px', // Trigger when section header is near top
            threshold: [0, 0.1, 0.5, 1],
          }
        );
        
        observers[env] = observer;
        
        // Observe only expanded section headers for this environment
        Object.keys(sectionRefs.current).forEach((key) => {
          const [refEnv, domain] = key.split('-');
          if (refEnv === env && sectionRefs.current[key] && expandedSections[env]?.[domain]) {
            observer.observe(sectionRefs.current[key]);
          }
        });
      });
    };
    
    // Setup observers with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(setupObservers, 200);
    
    return () => {
      clearTimeout(timeoutId);
      Object.values(observers).forEach((observer) => observer.disconnect());
    };
  }, [selectedLayer, expandedSections]);

  const handleScroll = (sourceEnv, scrollTop) => {
    if (isScrollingRef.current) return;
    
    isScrollingRef.current = true;
    
    const refs = [
      { env: 'dev', ref: devScrollRef },
      { env: 'test', ref: testScrollRef },
      { env: 'prod', ref: prodScrollRef },
    ];

    refs.forEach(({ env, ref }) => {
      if (env !== sourceEnv && ref.current) {
        ref.current.scrollTop = scrollTop;
      }
    });

      // Detect visible section based on scroll position
    const scrollContainer = refs.find(r => r.env === sourceEnv)?.ref?.current;
    if (scrollContainer) {
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const viewportTop = containerTop + 80; // Account for header
      
      // Find which section the user is currently in
      // A section is active if the viewport top is between its header and the next section's header
      
      const sections = [];
      Object.keys(sectionRefs.current).forEach((key) => {
        const [refEnv, sectionKey] = key.split('-');
        if (refEnv === sourceEnv && sectionRefs.current[key] && expandedSections[sourceEnv]?.[sectionKey]) {
          const sectionElement = sectionRefs.current[key];
          const sectionTop = sectionElement.getBoundingClientRect().top;
          
          sections.push({
            key: sectionKey,
            headerTop: sectionTop,
          });
        }
      });
      
      // Sort sections by position
      sections.sort((a, b) => a.headerTop - b.headerTop);
      
      // Find the active section: the one whose header is above viewport and before next section
      let activeSection = null;
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const nextSection = sections[i + 1];
        
        // Section is active if:
        // - Its header is above or at the viewport top (with some tolerance)
        // - AND either there's no next section OR next section's header hasn't reached viewport top yet
        if (section.headerTop <= viewportTop + 100) {
          if (!nextSection || nextSection.headerTop > viewportTop - 50) {
            activeSection = section.key;
            // Don't break - keep checking to find the most recent section
          }
        }
      }
      
      // Update visible section if we found an active one
      if (activeSection && expandedSections[sourceEnv]?.[activeSection]) {
        setVisibleSections((prev) => ({
          ...prev,
          [sourceEnv]: activeSection,
        }));
      } else if (sections.length === 0 || !sections.some(s => expandedSections[sourceEnv]?.[s.key])) {
        // Only clear if no sections are expanded
        setVisibleSections((prev) => ({
          ...prev,
          [sourceEnv]: null,
        }));
      }
    }

    requestAnimationFrame(() => {
      isScrollingRef.current = false;
    });
  };

  const handleStartEditing = async (environment) => {
    const result = await onStartSession(environment);
    if (result.success) {
      setEditingEnv(environment);
      setOriginalConfigs(prev => ({
        ...prev,
        [environment]: { ...configs[environment] }
      }));
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleCancel = async (environment) => {
    const result = await onCancelSession(environment);
    if (result.success) {
      setEditingEnv(null);
      setEditConfigs(prev => ({
        ...prev,
        [environment]: { ...configs[environment] }
      }));
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleSave = async (environment) => {
    const result = await onSaveSession(environment);
    if (result.success) {
      // Keep editing mode, just mark as saved
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleCommit = async (environment) => {
    const result = await onCommitSession(environment);
    if (result.success) {
      setEditingEnv(null);
      await onReload();
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleSaveAndCommit = async (environment) => {
    const result = await onSaveAndCommit(environment);
    if (result.success) {
      setEditingEnv(null);
      await onReload();
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleFieldChange = async (environment, fieldPath, value, event) => {
    const keys = fieldPath.split('.');
    const newConfig = { ...editConfigs[environment] };
    
    let current = newConfig;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setEditConfigs({
      ...editConfigs,
      [environment]: newConfig,
    });

    // Auto-save diff on blur or change
    const session = sessions[environment];
    if (session) {
      const oldValue = getFieldValue(originalConfigs[environment] || configs[environment], fieldPath);
      const newValue = value;
      
      // Only save if value actually changed
      if (oldValue !== newValue) {
        // Save diff on blur (if event is blur) or immediately (if onChange)
        if (event && event.type === 'blur') {
          await onSaveDiff(environment, fieldPath, oldValue, newValue);
        } else {
          // For onChange, debounce slightly or save immediately
          await onSaveDiff(environment, fieldPath, oldValue, newValue);
        }
      }
    }
  };

  const getFieldValue = (config, fieldPath) => {
    const keys = fieldPath.split('.');
    let value = config;
    for (const key of keys) {
      if (value === null || value === undefined) return '';
      value = value[key];
    }
    return value || '';
  };

  const toggleSection = (environment, section) => {
    // Toggle the section for the clicked environment
    const newState = !expandedSections[environment]?.[section];
    
    // Synchronize across all environments
    setExpandedSections({
      dev: {
        ...expandedSections.dev,
        [section]: newState,
      },
      test: {
        ...expandedSections.test,
        [section]: newState,
      },
      prod: {
        ...expandedSections.prod,
        [section]: newState,
      },
    });
    
    // Clear visible section if collapsing
    if (!newState) {
      setVisibleSections((prev) => ({
        ...prev,
        [environment]: null,
      }));
    }
  };

  // Helper function to resolve file path for a field based on environment
  const getFieldFilePath = (field, environment) => {
    if (!field.source || !fileMapping) return null;
    
    const source = field.source;
    const fileKey = source.file;
    const env = source.environment === 'all' ? environment : source.environment;
    
    if (fileMapping[fileKey]) {
      if (env === 'all' && fileMapping[fileKey].all) {
        return fileMapping[fileKey].all;
      } else if (fileMapping[fileKey][env]) {
        return fileMapping[fileKey][env];
      } else if (fileMapping[fileKey].all) {
        return fileMapping[fileKey].all;
      }
    }
    
    return null;
  };

  const renderSectionHeader = (environment, sectionKey, sectionTitle) => {
    const isExpanded = expandedSections[environment]?.[sectionKey] || false;
    const refKey = `${environment}-${sectionKey}`;
    
    return (
      <div
        ref={(el) => {
          if (el) {
            sectionRefs.current[refKey] = el;
          }
        }}
        data-section={sectionKey}
        onClick={() => toggleSection(environment, sectionKey)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.5rem 0',
          userSelect: 'none',
          marginTop: '1rem',
        }}
      >
        <h6 style={{ fontSize: '0.95rem', borderBottom: '2px solid #4a5568', paddingBottom: '4px', color: '#f7fafc', margin: 0, flex: 1 }}>
          {sectionTitle}
        </h6>
        <span style={{ color: '#4299e1', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
    );
  };

  const renderDomainSection = (environment, domain, config, fields) => {
    const isEditing = editingEnv === environment;
    const domainTitle = domain === 'core' ? 'Core Domain' : domain === 'ai' ? 'AI Domain' : 'Auth Domain';
    
    return (
      <div key={domain}>
        {renderSectionHeader(environment, domain, domainTitle)}
        <Collapse isOpen={expandedSections[environment]?.[domain] === true}>
          <div 
            data-section-content={domain}
            style={{ marginTop: '0.5rem', marginBottom: '1rem', visibility: 'visible', display: 'block', paddingLeft: '0.5rem' }}>
            {fields.map((field) => {
              const filePath = getFieldFilePath(field, environment);
              const variableName = field.source?.variable || field.key;
              const infoId = `info-${environment}-${field.key}`;
              const tooltipText = filePath 
                ? `File: ${filePath}\nVariable: ${variableName}`
                : `Variable: ${variableName}`;
              
              return (
              <FormGroup key={field.key} style={{ marginBottom: '0.75rem' }}>
                <Label style={{ fontWeight: '600', color: '#e2e8f0', fontSize: '0.875rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {field.label}
                  {field.source && (
                    <>
                      <span
                        id={infoId}
                        style={{
                          cursor: 'help',
                          color: '#4299e1',
                          fontSize: '0.875rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          border: '1px solid #4299e1',
                          backgroundColor: 'transparent',
                          lineHeight: '1',
                        }}
                      >
                        i
                      </span>
                      <UncontrolledTooltip placement="top" target={infoId} delay={{ show: 200, hide: 100 }}>
                        <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                          {tooltipText}
                        </div>
                      </UncontrolledTooltip>
                    </>
                  )}
                </Label>
                  {field.type === 'select' || field.type === 'boolean' ? (
                    <Input
                      type="select"
                      value={getFieldValue(isEditing ? editConfigs[environment] : config, field.key)?.toString()}
                      onChange={(e) => handleFieldChange(environment, field.key, e.target.value === 'true', e)}
                      onBlur={(e) => handleFieldChange(environment, field.key, e.target.value === 'true', e)}
                      disabled={!isEditing}
                      style={!isEditing ? { backgroundColor: '#4a5568', color: '#f7fafc', fontWeight: '500', border: '1px solid #718096', fontSize: '0.875rem', padding: '0.375rem 0.75rem' } : { backgroundColor: '#2d3748', color: '#f7fafc', fontWeight: '400', border: '2px solid #4299e1', boxShadow: '0 0 0 0.2rem rgba(66,153,225,.25)', fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                    >
                      {(field.options || [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }]).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Input>
                  ) : field.type === 'number' ? (
                  <Input
                    type="number"
                    value={getFieldValue(isEditing ? editConfigs[environment] : config, field.key)}
                    onChange={(e) => handleFieldChange(environment, field.key, parseInt(e.target.value), e)}
                    onBlur={(e) => handleFieldChange(environment, field.key, parseInt(e.target.value), e)}
                    disabled={!isEditing}
                    style={!isEditing ? { backgroundColor: '#4a5568', color: '#f7fafc', fontWeight: '500', border: '1px solid #718096', fontSize: '0.875rem', padding: '0.375rem 0.75rem' } : { backgroundColor: '#2d3748', color: '#f7fafc', fontWeight: '400', border: '2px solid #4299e1', boxShadow: '0 0 0 0.2rem rgba(66,153,225,.25)', fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                  />
                ) : (
                  <Input
                    type="text"
                    value={getFieldValue(isEditing ? editConfigs[environment] : config, field.key)}
                    onChange={(e) => handleFieldChange(environment, field.key, e.target.value, e)}
                    onBlur={(e) => handleFieldChange(environment, field.key, e.target.value, e)}
                    disabled={!isEditing || field.readOnly}
                    style={!isEditing ? { backgroundColor: '#4a5568', color: '#f7fafc', fontWeight: '500', border: '1px solid #718096', fontSize: '0.875rem', padding: '0.375rem 0.75rem' } : { backgroundColor: '#2d3748', color: '#f7fafc', fontWeight: '400', border: '2px solid #4299e1', boxShadow: '0 0 0 0.2rem rgba(66,153,225,.25)', fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                  />
                )}
              </FormGroup>
            );
            })}
          </div>
        </Collapse>
      </div>
    );
  };

  const getFieldsForLayer = (layer) => {
    // Use field mapping from backend if available, otherwise fallback to empty
    if (fieldMapping) {
      return fieldMapping;
    }
    
    // Fallback: return empty structure if mapping not loaded yet
    return { core: [], ai: [], auth: [] };
  };

  const renderConfigForm = (environment, config) => {
    const isEditing = editingEnv === environment;
    const canEdit = permissions[environment]?.edit || false;
    const fieldsForLayer = getFieldsForLayer(selectedLayer);

    return (
      <Card style={{ backgroundColor: '#2d3748', border: 'none', height: '100%', display: 'flex', flexDirection: 'column', maxHeight: '550px' }}>
        <CardHeader style={{ backgroundColor: '#1a202c', borderBottom: '1px solid #4a5568', padding: '0.75rem 1rem', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h6 style={{ fontSize: '1.2rem', color: '#f7fafc', margin: 0, display: 'inline-block' }}>
                {environment === 'dev' ? 'Development' : environment === 'test' ? 'Test' : 'Production'}
                {hasUnsavedChanges[environment] && (
                  <span style={{ marginLeft: '0.5rem', color: '#fbbf24', fontSize: '1rem' }}>*</span>
                )}
              </h6>
              {visibleSections[environment] && (
                <span style={{ 
                  fontSize: '0.85rem', 
                  color: '#a0aec0', 
                  marginLeft: '0.75rem',
                  fontStyle: 'italic',
                  fontWeight: 'normal'
                }}>
                  • {visibleSections[environment] === 'core' ? 'Core Domain' : 
                      visibleSections[environment] === 'ai' ? 'AI Domain' : 
                      visibleSections[environment] === 'auth' ? 'Auth Domain' : ''}
                </span>
              )}
            </div>
            {isEditing ? (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  size="sm"
                  color="info"
                  onClick={() => handleSave(environment)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  color="success"
                  onClick={() => handleCommit(environment)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  Commit
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  onClick={() => handleSaveAndCommit(environment)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  Save & Commit
                </Button>
                <Button
                  size="sm"
                  color="secondary"
                  onClick={() => handleCancel(environment)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              canEdit && (
                <Button
                  size="sm"
                  color="primary"
                  onClick={() => handleStartEditing(environment)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  Edit
                </Button>
              )
            )}
          </div>
        </CardHeader>
        
        <CardBody style={{ overflow: 'hidden', padding: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div
            ref={environment === 'dev' ? devScrollRef : environment === 'test' ? testScrollRef : prodScrollRef}
            style={{ flex: 1, maxHeight: "500px", overflowY: "auto", overflowX: "hidden", backgroundColor: '#2d3748', color: '#e2e8f0', padding: '1rem' }}
            onScroll={(e) => {
              const scrollTop = e.target.scrollTop;
              if (!isScrollingRef.current) {
                handleScroll(environment, scrollTop);
              }
            }}
          >
            <Form style={{ backgroundColor: '#2d3748', color: '#e2e8f0' }}>
              {/* Render domains based on selected layer */}
              {Object.keys(fieldsForLayer).map((domain) => {
                const domainFields = fieldsForLayer[domain];
                if (domainFields.length === 0) return null;
                return renderDomainSection(environment, domain, config, domainFields);
              })}
            </Form>
          </div>
        </CardBody>
      </Card>
    );
  };

  return (
    <Row style={{ margin: 0 }}>
      <Col md={4} style={{ padding: '10px' }}>
        {renderConfigForm('dev', configs.dev)}
      </Col>
      <Col md={4} style={{ padding: '10px' }}>
        {renderConfigForm('test', configs.test)}
      </Col>
      <Col md={4} style={{ padding: '10px' }}>
        {renderConfigForm('prod', configs.prod)}
      </Col>
    </Row>
  );
}

export default ConfigViewer;
